import React, { useEffect, useRef, useState } from "react";
import { Camera, Upload, User } from "lucide-react";
import { api } from "../lib/api";
import { isAdmin } from "../lib/customers";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";

export const Avatar = ({ src, name, size = "w-12 h-12", testid }) => (
  <div data-testid={testid} className={`${size} rounded-full overflow-hidden bg-teal-100 text-teal-800 flex items-center justify-center shrink-0 border-2 border-white shadow-sm`}>
    {src ? <img src={src} alt={name} className="w-full h-full object-cover" /> :
      name ? <span className="font-semibold">{name.trim().charAt(0).toUpperCase()}</span> : <User className="w-1/2 h-1/2" />}
  </div>
);

const YesNo = ({ label, value, onChange, testid }) => (
  <div>
    <Label className="text-xs">{label}</Label>
    <div className="mt-1 flex gap-2">
      {[true, false].map((v) => (
        <button key={String(v)} type="button" onClick={() => onChange(v)} data-testid={`${testid}-${v ? "yes" : "no"}`}
          className={`px-4 py-1.5 rounded-full text-sm border transition-colors ${value === v ? "bg-teal-700 text-white border-teal-700" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"}`}>
          {v ? "Haan" : "Nahi"}
        </button>
      ))}
    </div>
  </div>
);

const F = ({ label, k, form, set, type = "text", span, ...rest }) => (
  <div className={span ? "sm:col-span-2" : ""}>
    <Label className="text-xs">{label}</Label>
    <Input type={type} value={form[k] || ""} onChange={(e) => set(k, e.target.value)} data-testid={`cust-${k}`} {...rest} />
  </div>
);

const Sel = ({ label, k, form, set, options }) => (
  <div>
    <Label className="text-xs">{label}</Label>
    <select value={form[k] || ""} onChange={(e) => set(k, e.target.value)} data-testid={`cust-${k}`}
      className="mt-0 w-full h-9 rounded-md border border-input bg-transparent px-3 text-sm">
      <option value="">—</option>
      {options.map((o) => <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>)}
    </select>
  </div>
);

const Section = ({ title, children }) => (
  <div>
    <div className="text-[11px] uppercase tracking-widest font-semibold text-teal-700 mb-2">{title}</div>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>
  </div>
);

const PhotoPicker = ({ preview, name, onPhoto }) => {
  const camRef = useRef(null);
  const upRef = useRef(null);
  const pick = (e) => { const f = e.target.files?.[0]; if (f) onPhoto(f); e.target.value = ""; };
  return (
    <div className="flex items-center gap-4">
      <Avatar src={preview} name={name} size="w-20 h-20" testid="cust-form-avatar" />
      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={() => camRef.current?.click()} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-teal-700 text-white text-xs" data-testid="cust-camera-btn"><Camera className="w-3 h-3" /> Photo khinchein</button>
        <button type="button" onClick={() => upRef.current?.click()} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-slate-300 text-xs" data-testid="cust-upload-btn"><Upload className="w-3 h-3" /> Upload</button>
        <input ref={camRef} type="file" accept="image/*" capture="user" className="hidden" onChange={pick} data-testid="cust-camera-input" />
        <input ref={upRef} type="file" accept="image/*" className="hidden" onChange={pick} data-testid="cust-photo-input" />
      </div>
    </div>
  );
};

export const CustomerForm = ({ form, setForm, preview, onPhoto }) => {
  const [branches, setBranches] = useState([]);
  const set = (k, v) => setForm({ ...form, [k]: v });
  const p = { form, set };
  useEffect(() => {
    if (isAdmin()) api.get("/admin/branches").then((r) => setBranches(r.data || [])).catch(() => {});
  }, []);
  return (
    <div className="space-y-5 text-sm">
      <PhotoPicker preview={preview} name={form.name} onPhoto={onPhoto} />
      <Section title="Basic">
        <F label="Customer Full Name *" k="name" {...p} />
        <F label="Father's Name" k="father_name" {...p} />
        <F label="Mother's Name" k="mother_name" {...p} />
        <F label="Date of Birth" k="dob" type="date" {...p} />
        <Sel label="Gender" k="gender" options={["Male", "Female", "Other"]} {...p} />
        <Sel label="Customer Type" k="customer_type" options={["Student", "General", "Business", "Other"]} {...p} />
        <F label="Mobile Number *" k="mobile" inputMode="tel" {...p} />
        <F label="Alternate Mobile" k="alt_mobile" inputMode="tel" {...p} />
        <F label="Email ID" k="email" type="email" {...p} />
        <F label="Instagram ID" k="insta_id" placeholder="@username" {...p} />
        <F label="Educational Qualification" k="qualification" {...p} />
        {isAdmin() && <Sel label="Customer Branch (— = Main Head Office)" k="branch_id" options={branches.map((b) => ({ value: b.id, label: b.name_en }))} {...p} />}
      </Section>
      <Section title="Address">
        <div className="sm:col-span-2"><Label className="text-xs">Full Address</Label><Textarea rows={2} value={form.address || ""} onChange={(e) => set("address", e.target.value)} data-testid="cust-address" /></div>
        <F label="Village" k="village" {...p} />
        <F label="Post Office" k="post_office" {...p} />
        <F label="Panchayat" k="panchayat" {...p} />
        <F label="Block" k="block" {...p} />
        <F label="District" k="district" {...p} />
        <F label="State" k="state" {...p} />
        <F label="PIN Code" k="pin" inputMode="numeric" {...p} />
      </Section>
      <Section title="Enquiry / Follow-up">
        <F label="Kis vishay me poochhtachh" k="enquiry_subject" span placeholder="e.g. PAN card, IGNOU admission" {...p} />
        <F label="Dubara kab aayega" k="revisit_at" type="datetime-local" {...p} />
        <div />
        <YesNo label="Call karke batana hai?" value={!!form.call_back} onChange={(v) => set("call_back", v)} testid="cust-callback" />
        <YesNo label="Follow-up me daalna hai?" value={!!form.add_followup} onChange={(v) => set("add_followup", v)} testid="cust-followup" />
      </Section>
      <Section title="Consent & Data Retention">
        <label className="sm:col-span-2 flex items-start gap-2 text-xs text-slate-700">
          <input type="checkbox" checked={!!form.consent} onChange={(e) => set("consent", e.target.checked)} className="mt-0.5" data-testid="cust-consent" />
          Customer ne apne documents / details Ashish Point me surakshit rakhne ki sahmati di hai.
        </label>
        <F label="Data rakhne ki antim tareekh (optional)" k="retention_until" type="date" {...p} />
      </Section>
    </div>
  );
};
